import { useTitle } from "@/hooks/use-title.hook.ts";
import {
  useOrganizationMetricCalculations,
  useOrganizationUpdateMetricCalculations,
} from "@/queries/settings/use-organization.query.ts";
import { useOrganizationListByType } from "@/queries/use-organization-lists.query.ts";
import { ApiOrganizationListType } from "@/types/organization-list.types.ts";
import { ApiOrganizationSettingsMetricCalculations } from "@/types/organization.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Multiselect, Page, SelectItem, Stack, Title, useToasts } from "@skailer-com/uikit";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export default function MetricsConfiguration() {
  const { t } = useTranslation();
  const { showInfo } = useToasts();

  useTitle(t("core:settings_analytics_title"), t("common:settings"));

  const { data: metricCalculations } = useOrganizationMetricCalculations();
  const { data: leaveTypesList } = useOrganizationListByType(
    ApiOrganizationListType.termination_leave_types
  );

  const leaveTypeOptions = useMemo<SelectItem[]>(() => {
    return (leaveTypesList?.items || [])
      .filter((item) => !item.is_archived)
      .map((item) => ({ id: item.id, title: item.title }));
  }, [leaveTypesList]);

  const schemaFields = z.object({
    voluntary_leave_type_ids: z.array(z.number()),
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof schemaFields>>({
    mode: "onSubmit",
    resolver: zodResolver(schemaFields),
    defaultValues: {
      voluntary_leave_type_ids: [],
    },
  });

  useEffect(() => {
    if (metricCalculations) {
      reset({
        voluntary_leave_type_ids: metricCalculations.voluntary_leave_type_ids || [],
      });
    }
  }, [metricCalculations]);

  const updateMetricCalculations = useOrganizationUpdateMetricCalculations(control);
  const onSubmit = handleSubmit(async (formData) => {
    const payload: ApiOrganizationSettingsMetricCalculations = {
      voluntary_leave_type_ids: formData.voluntary_leave_type_ids,
    };

    try {
      await updateMetricCalculations.mutateAsync(payload);
      showInfo({ title: t("common:you_changes_were_saved") });
    } catch {
      return;
    }
  });

  return (
    <Page>
      <Page.Header
        title={t("core:settings_analytics_title")}
        showBack={true}
        navigateTo={"/settings"}
      />
      <Page.Content>
        <Stack gap={"xl"} className={"container-sm mx-auto"}>
          <Title
            size={"lg"}
            paddingBottom
            paddingTop
            header={t("core:settings_analytics_title")}
            caption={t("core:settings_metrics_configuration_caption")}
          />

          <Title
            size={"xs"}
            header={t("core:settings_metrics_configuration_retention_title")}
            caption={t("core:settings_metrics_configuration_retention_caption")}
          />
          <form onSubmit={onSubmit}>
            <Controller
              key={"voluntary_leave_type_ids"}
              render={({ field: { onChange, value } }) => (
                <Multiselect
                  label={t("core:termination_leave_types")}
                  options={leaveTypeOptions}
                  values={value}
                  onChange={onChange}
                  searchable
                  isClearable
                  error={errors.voluntary_leave_type_ids?.message as string}
                  emptyTitle={t("common:select")}
                />
              )}
              control={control}
              name={"voluntary_leave_type_ids"}
            />
            <Stack gap={"sm"} className="mt-4">
              <Button
                size={"lg"}
                className={"w-full"}
                type={"submit"}
                isLoading={updateMetricCalculations.isPending}
              >
                {t("common:save")}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Page.Content>
    </Page>
  );
}
